import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { isAuthenticated, API } from "../utils/api";
import { ArrowRight, Check, ChevronDown, Menu, X, Star, Zap, Shield, Cpu, Headphones, Award, TrendingUp, Globe } from "lucide-react";
import { toast } from "sonner";
import {
  useReveal, MagBtn, MouseTrail, AudioPlayer, WhatsAppFloat,
  MarqueeStrip, WebCube, StepItem,
  SHOWCASE, STEPS, TESTIMONIALS,
  startPlanCheckout,
} from "./LandingPage.Components";

/* ── Main ───────────────────────────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cubeRx, setCubeRx] = useState(20);
  const [cubeRy, setCubeRy] = useState(30);
  const [activeTab, setActiveTab] = useState(0);
  const [tabHovered, setTabHovered] = useState(false);
  const [lineProgress, setLineProgress] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [waNumber, setWaNumber] = useState("");
  const heroRef = useRef(null);
  const processRef = useRef(null);

  const [showcaseRef, showcaseVis] = useReveal(0.1);
  const [pricingRef, pricingVis] = useReveal(0.1);
  const [testimonialsRef, testimonialsVis] = useReveal(0.1);
  const [proofRef, proofVis] = useReveal(0.1);

  useEffect(() => {
    if (isAuthenticated()) navigate("/home", { replace: true });
  }, [navigate]);

  useEffect(() => {
    fetch(`${API}/public/contact`)
      .then(r => r.json())
      .then(d => setWaNumber(d.whatsapp_number || ""))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      if (processRef.current) {
        const rect = processRef.current.getBoundingClientRect();
        const p = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        setLineProgress(Math.max(0, Math.min(1, p)));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleHeroMouse = useCallback((e) => {
    if (!heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    setCubeRx(20 + ((e.clientY - r.top - r.height/2) / (r.height/2)) * -14);
    setCubeRy(30 + ((e.clientX - r.left - r.width/2) / (r.width/2)) * 16);
  }, []);

  useEffect(() => {
    if (tabHovered) return;
    const t = setInterval(() => setActiveTab(p => (p+1) % SHOWCASE.length), 5000);
    return () => clearInterval(t);
  }, [tabHovered]);

  const handlePlan = async (plan) => {
    setCheckoutLoading(plan);
    try { await startPlanCheckout(plan); }
    catch(e) { toast.error(e.message || "Error al iniciar el pago"); }
    finally { setCheckoutLoading(null); }
  };

  const active = SHOWCASE[activeTab];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      <MouseTrail />
      <AudioPlayer />
      <WhatsAppFloat waNumber={waNumber} />

      {/* ══ NAV ════════════════════════════════════════════════════════════════ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled?"bg-[#0D0D0D]/96 backdrop-blur-2xl border-b border-white/[0.06]":""}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="SitiosYA" className="h-24 w-24 object-contain" style={{ filter: "drop-shadow(0 0 16px rgba(255,199,0,0.6)) drop-shadow(0 0 32px rgba(255,199,0,0.3))" }} onError={e=>{e.target.style.display="none";}} />
          </div>
          <div className="hidden md:flex items-center gap-3 lg:gap-5">
            {/* Links de navegación pegados a los botones */}
            <div className="flex items-center gap-3 lg:gap-5 text-sm font-semibold text-gray-400 mr-1">
              <a href="#beneficios" className="hover:text-white transition-colors whitespace-nowrap">Beneficios</a>
              <a href="#como-funciona" className="hover:text-white transition-colors whitespace-nowrap hidden lg:block">Cómo funciona</a>
              <a href="#precio" className="hover:text-white transition-colors whitespace-nowrap">Precios</a>
              <Link to="/resellers" className="hover:text-[#ffc700] transition-colors font-bold whitespace-nowrap">Revendedores</Link>
            </div>
            <button onClick={() => navigate("/login")} data-testid="nav-login-btn"
              className="bg-[#3199BA] hover:bg-[#2888a8] text-white font-black px-4 py-2.5 rounded-xl text-sm transition-all whitespace-nowrap">
              Ingresar
            </button>
            <MagBtn testId="cta-nav-btn" onClick={() => document.getElementById("precio")?.scrollIntoView({behavior:"smooth"})}
              className="bg-[#FFC000] hover:bg-[#e6b000] active:scale-[0.97] text-black text-sm font-black px-4 py-2.5 rounded-xl flex items-center gap-1.5 btn-cta-pulse whitespace-nowrap">
              Quiero mi web <ArrowRight size={13} />
            </MagBtn>
          </div>
          <button className="md:hidden p-2" onClick={() => setMenuOpen(o=>!o)} aria-label="menu">
            {menuOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#0D0D0D] border-t border-white/[0.05] px-5 py-5 space-y-1">
            {[["Beneficios","#beneficios"],["Cómo funciona","#como-funciona"],["Precios","#precio"]].map(([l,h]) => (
              <a key={l} href={h} className="block py-3 text-sm font-semibold text-gray-300 border-b border-white/[0.04]" onClick={() => setMenuOpen(false)}>{l}</a>
            ))}
            <Link to="/resellers" className="block py-3 text-sm font-bold text-[#ffc700] border-b border-white/[0.04]" onClick={() => setMenuOpen(false)}>Revendedores</Link>
            <button onClick={() => { navigate("/login"); setMenuOpen(false); }} className="block py-3 text-sm font-semibold text-gray-300 border-b border-white/[0.04] w-full text-left">Ingresar</button>
            <div className="pt-4">
              <button onClick={() => { document.getElementById("precio")?.scrollIntoView({behavior:"smooth"}); setMenuOpen(false); }} className="w-full bg-[#ffc700] text-black font-black py-3.5 rounded-xl text-sm">Quiero mi página web →</button>
            </div>
          </div>
        )}
      </nav>

      {/* ══ HERO ═══════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} onMouseMove={handleHeroMouse}
        className="relative min-h-screen flex flex-col lg:flex-row items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 grid-pattern pointer-events-none" />
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#ffc700]/[0.04] rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-[#0074b7]/[0.03] rounded-full blur-[120px] pointer-events-none" />

        {/* Copy */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-5 sm:px-10 lg:pl-16 xl:pl-24 py-16 lg:py-0 max-w-3xl">
          <div className="inline-flex w-fit items-center gap-2 border border-[#ffc700]/30 text-[#ffc700] text-[10px] font-black uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-8">
            <span>🔥</span> Un nuevo nivel se desbloquea hoy
          </div>

          {/* H1 — 2 líneas */}
          <h1 className="font-black uppercase leading-[0.92] tracking-[-0.03em] mb-6" style={{ fontSize:"clamp(1.9rem,3.8vw,3.4rem)" }}>
            <span className="block text-white">
              ¡SI QUERÉS QUE TE{" "}
              <span style={{ WebkitTextStroke:"2px #FFC000", color:"transparent" }}>VALOREN,</span>
            </span>
            <span className="block text-white">VALORÁ TU TRABAJO!</span>
          </h1>

          {/* Advertencia */}
          <div className="mb-8 max-w-lg rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" fill="rgba(245,158,11,0.2)" stroke="#f59e0b" strokeWidth="1.8" strokeLinejoin="round"/>
                <line x1="12" y1="9" x2="12" y2="13" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="12" y1="17" x2="12.01" y2="17" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="text-xs text-amber-300 font-semibold leading-relaxed">
              Tus competidores ya están en Google y Apps de IA captando clientes. ¿Y vos? Siglo 21 y no tenés tu sitio web profesional?
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <MagBtn testId="hero-cta-btn" onClick={() => document.getElementById("precio")?.scrollIntoView({behavior:"smooth"})}
              className="bg-[#ffc700] hover:bg-[#e6b400] active:scale-[0.97] text-black font-black px-7 py-4 rounded-xl text-sm transition-all flex items-center justify-center gap-2 btn-cta-pulse">
              Quiero mi página web <ArrowRight size={16}/>
            </MagBtn>
            <a href="#como-funciona" className="btn-fill-left flex items-center justify-center gap-2 text-gray-400 hover:text-white font-bold text-sm px-4 py-4 transition-colors border border-white/[0.07] rounded-xl">
              Cómo funciona <ChevronDown size={15}/>
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 font-semibold">
            <span className="font-black text-sm" style={{ color:"#3199BA" }}>Diseñador de IA</span>
            {["Dominio incluido","Soporte real","Cancelás cuando querés"].map(t=>(
              <div key={t} className="flex items-center gap-1.5">
                <Check size={12} className="text-[#FFC000]"/>{t}
              </div>
            ))}
          </div>
        </div>

        {/* Web Cube */}
        <div className="hidden lg:flex flex-1 items-center justify-center relative pr-16 min-h-[500px]">
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-72 h-72 rounded-full border border-[#ffc700]/[0.07] animate-[spin_25s_linear_infinite]" />
              <div className="absolute w-96 h-96 rounded-full border border-[#ffc700]/[0.03] animate-[spin_40s_linear_reverse_infinite]" />
            </div>
            <div className="w-64 h-64 flex items-center justify-center">
              <WebCube rx={cubeRx} ry={cubeRy} />
            </div>
            <div className="absolute -top-8 -right-14 bg-[#111] border border-[#ffc700]/20 rounded-2xl px-4 py-3 animate-[float-price_3s_ease-in-out_infinite]">
              <p className="text-[#ffc700] text-2xl font-black leading-none">5min</p>
              <p className="text-gray-500 text-[10px] font-semibold mt-0.5">Tu negocio online</p>
            </div>
            {/* Google review card */}
            <div className="absolute -bottom-6 -left-16 bg-white rounded-2xl px-4 py-3 animate-[float-y_4s_ease-in-out_infinite_0.5s] shadow-xl shadow-black/40 min-w-[140px]">
              <div className="flex items-center gap-1.5 mb-1">
                <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-gray-700 text-[10px] font-black">Google</span>
              </div>
              <div className="flex gap-0.5 mb-1">
                {[1,2,3,4,5].map(i=><Star key={i} size={11} className="text-[#FBBC04] fill-[#FBBC04]"/>)}
              </div>
              <p className="text-gray-800 text-[11px] font-black">4.9 <span className="text-gray-400 font-medium">· 127 reseñas</span></p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-px h-10 bg-gradient-to-b from-transparent to-[#ffc700]/30" />
        </div>
      </section>

      {/* ══ MARQUEE ════════════════════════════════════════════════════════════ */}
      <MarqueeStrip />

      {/* ══ PRUEBA SOCIAL ═══════════════════════════════════════════════════════ */}
      <section ref={proofRef} className="pt-14 pb-4 sm:pt-20 sm:pb-6 section-darker relative overflow-hidden">
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item text-center mb-10 ${proofVis?"visible":""}`}>
            <h2 className="font-black leading-[1.15] max-w-3xl mx-auto text-gray-100 italic" style={{ fontSize:"clamp(1.2rem,2.5vw,2rem)" }}>
              "Tener una web es como tener tu propio local físico pero en internet — a un costo infinitamente menor, generando la misma o más confianza."
            </h2>
          </div>
          {/* Browser mockup full */}
          <div className={`reveal-item delay-1 ${proofVis?"visible":""} relative rounded-3xl overflow-hidden border border-white/[0.06] shadow-[0_40px_100px_rgba(0,0,0,0.6)]`}>
            {/* Browser chrome */}
            <div className="bg-[#111] px-5 py-3 flex items-center gap-3 border-b border-white/[0.06]">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                <div className="w-3 h-3 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#0D0D0D] rounded-lg h-6 flex items-center px-3">
                <span className="text-[10px] text-gray-500 font-mono">mipasteleria.com</span>
              </div>
            </div>
            {/* Site preview */}
            <div className="relative min-h-[280px] sm:min-h-[360px] bg-gradient-to-br from-[#0a192f] via-[#0D0D0D] to-[#0a0a0a] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-transparent pointer-events-none" />
              <div className="flex items-center justify-between px-6 sm:px-10 py-4 border-b border-white/[0.05]">
                <span className="font-black text-white text-sm">Café<span className="text-[#ffc700]">Luna</span></span>
                <div className="hidden sm:flex gap-6 text-[11px] text-gray-400 font-semibold">
                  {["Menú","Reservas","Nosotros","Contacto"].map(i=><span key={i}>{i}</span>)}
                </div>
                <button className="bg-[#ffc700] text-black text-[10px] font-black px-4 py-1.5 rounded-lg">Reservar</button>
              </div>
              <div className="px-6 sm:px-10 py-8 sm:py-12">
                <div className="inline-flex items-center gap-1.5 bg-blue-500/15 border border-blue-500/25 text-blue-400 text-[9px] font-bold px-3 py-1 rounded-full mb-4">
                  <Zap size={8}/> ABIERTO HOY · 8AM — 10PM
                </div>
                <h3 className="text-white font-black leading-tight mb-3" style={{ fontSize:"clamp(1.5rem,4vw,3rem)" }}>
                  El mejor café<br /><span className="text-[#ffc700]">de tu barrio.</span>
                </h3>
                <p className="text-gray-400 text-sm mb-6 max-w-md leading-relaxed">Café artesanal, pasteles caseros y el mejor ambiente. Montevideo, Uruguay.</p>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-[#ffc700] text-black text-sm font-black px-5 py-2.5 rounded-xl">Ver el menú</button>
                  <button className="border border-white/20 text-white text-sm font-semibold px-5 py-2.5 rounded-xl">Reservar mesa</button>
                </div>
                {/* Google review card — slides in from right on scroll */}
                <div className={`absolute right-6 sm:right-12 top-1/3 bg-white rounded-2xl p-4 w-36 sm:w-44 hidden sm:block shadow-2xl transition-all duration-700 delay-200 ${proofVis?"opacity-100 translate-x-0":"opacity-0 translate-x-20"}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <svg width="13" height="13" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span className="text-gray-700 text-[10px] font-black">Google</span>
                    <span className="ml-auto text-gray-800 text-[10px] font-black">4.9 ★</span>
                  </div>
                  <div className="flex gap-0.5 mb-1.5">
                    {[1,2,3,4,5].map(i=><Star key={i} size={10} className="text-[#FBBC04] fill-[#FBBC04]"/>)}
                  </div>
                  <p className="text-gray-600 text-[9px] italic leading-snug">"Increíble experiencia, volveré siempre."</p>
                  <p className="text-gray-400 text-[8px] mt-1">— Carolina P. · hace 2 días</p>
                </div>
              </div>
              {/* Badge — slides up from bottom on scroll, fuera del área de preview */}
              <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-[#ffc700]/10 border border-[#ffc700]/20 rounded-full px-3 py-1.5 transition-all duration-700 delay-400 ${proofVis?"opacity-100 translate-y-0":"opacity-0 translate-y-10"}`}>
                <Zap size={9} className="text-[#ffc700]"/>
                <span className="text-[#ffc700] text-[9px] font-black">Creado con SitiosYA · 5min</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ BRAND STORY — Awwwards section ══════════════════════════════════════ */}
      <section className="py-20 sm:py-28 section-dark relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none"/>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#3199BA] mb-5">Construido para destacarte</p>
          <h2 className="font-black uppercase leading-[0.88] tracking-[-0.03em] mb-7" style={{ fontSize:"clamp(2.4rem,5vw,4.5rem)" }}>
            <span className="block text-white">VISIBLE.</span>
            <span className="block" style={{ WebkitTextStroke:"2px #FFC000", color:"transparent" }}>PROFESIONAL.</span>
            <span className="block text-white">CONFIABLE.</span>
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-xl mx-auto">
            Destacá tu negocio en el lugar donde están tus clientes. Una web profesional no es un lujo — es la diferencia entre ser elegido o ser ignorado.
          </p>
          {/* AI Builder badge — centrado */}
          <div className="inline-flex items-start gap-3 bg-[#3199BA]/[0.06] border border-[#3199BA]/15 rounded-2xl px-5 py-4 text-left max-w-md">
            <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background:"rgba(49,153,186,0.12)" }}>
              <Cpu size={17} className="text-[#3199BA]"/>
            </div>
            <div>
              <p className="text-xs font-black text-white mb-0.5">Construido con nuestro propio Constructor IA</p>
              <p className="text-[10px] text-gray-500 leading-relaxed">Este sitio web fue creado con la misma tecnología que usará tu negocio. El constructor IA de SitiosYA en acción.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SHOWCASE TABS ══════════════════════════════════════════════════════ */}
      <section id="beneficios" ref={showcaseRef} className="pb-24 sm:pb-32 section-dark relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item mb-12 sm:mb-16 ${showcaseVis?"visible":""}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0074b7] mb-3">Para todo tipo de negocio</p>
            <h2 className="font-black uppercase leading-[0.9]" style={{ fontSize:"clamp(2rem,4.5vw,4.5rem)" }}>
              Cualquier negocio.<br /><span className="text-gray-600">Una web que convierte.</span>
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-14" onMouseEnter={()=>setTabHovered(true)} onMouseLeave={()=>setTabHovered(false)}>
            {/* Tabs */}
            <div className="flex lg:flex-col gap-2 overflow-x-auto scrollbar-none lg:w-60 flex-shrink-0">
              {SHOWCASE.map((tab,i) => (
                <button key={tab.num} onClick={()=>setActiveTab(i)} data-testid={`showcase-tab-${i}`}
                  className={`flex-shrink-0 text-left p-4 rounded-2xl border transition-all duration-300 ${activeTab===i?"border-[#ffc700]/30 bg-[#ffc700]/[0.05]":"border-white/[0.06] bg-white/[0.02] hover:border-white/[0.1]"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-black font-mono transition-colors ${activeTab===i?"text-[#ffc700]":"text-gray-600"}`}>{tab.num}</span>
                    <span className={`text-sm font-black uppercase tracking-wide transition-colors ${activeTab===i?"text-white":"text-gray-500"}`}>{tab.title}</span>
                  </div>
                  {activeTab===i && <p className="text-xs text-gray-500 mt-2 ml-9 leading-relaxed">{tab.subtitle}</p>}
                </button>
              ))}
            </div>
            {/* Image panel */}
            <div className="relative flex-1 min-h-[320px] sm:min-h-[460px] rounded-3xl overflow-hidden border border-white/[0.06]">
              {SHOWCASE.map((tab,i) => (
                <div key={tab.num} className="absolute inset-0 transition-opacity duration-700" style={{ opacity:activeTab===i?1:0, zIndex:activeTab===i?1:0 }}>
                  <img src={tab.img} alt={tab.title} className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/50 to-transparent"/>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/60 to-transparent"/>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 z-10">
                    <div className="inline-flex items-center px-3 py-1.5 rounded-full border mb-3" style={{ background:tab.color+"20", borderColor:tab.color+"40" }}>
                      <span className="text-[10px] font-black uppercase tracking-widest" style={{ color:tab.color }}>{tab.title}</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-black text-white mb-3">{tab.subtitle}</p>
                    <div className="flex flex-wrap gap-2">
                      {tab.features.map(f=>(
                        <div key={f} className="flex items-center gap-1.5 bg-white/[0.07] border border-white/[0.1] rounded-lg px-2.5 py-1">
                          <Check size={10} className="text-[#ffc700]"/>
                          <span className="text-xs text-gray-200 font-semibold">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/[0.04] z-10">
                <div className="h-full bg-[#ffc700] transition-all duration-[5000ms] linear" style={{ width:tabHovered?"0%":"100%" }}/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROCESO ════════════════════════════════════════════════════════════ */}
      <section id="como-funciona" ref={processRef} className="py-24 sm:py-36 section-darker relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none"/>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item text-center mb-16 ${showcaseVis?"visible":""}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FF4F00] mb-3">Cómo funciona</p>
            <h2 className="font-black uppercase leading-[0.9]" style={{ fontSize:"clamp(2rem,4.5vw,4.5rem)" }}>Tu web, en 4 pasos.</h2>
          </div>
          <div className="relative">
            <div className="absolute left-7 sm:left-1/2 top-0 bottom-0 w-px bg-white/[0.05]">
              <div className="absolute top-0 left-0 w-full bg-gradient-to-b from-[#ffc700] to-[#FF4F00] origin-top" style={{ height:`${lineProgress*100}%`, transition:"height 0.1s ease-out" }}/>
            </div>
            <div className="space-y-12 sm:space-y-0">
              {STEPS.map((s,i) => <StepItem key={s.num} step={s} index={i}/>)}
            </div>
          </div>
        </div>
      </section>

      {/* ══ PRECIO — 2 PLANES ══════════════════════════════════════════════════ */}
      <section id="precio" ref={pricingRef} className="py-24 sm:py-36 section-dark relative overflow-hidden">
        <div className="absolute inset-0 noise pointer-events-none"/>
        <div className={`reveal-item max-w-4xl mx-auto px-5 ${pricingVis?"visible":""}`}>
          <div className="text-center mb-12">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#ffc700] mb-3">Planes</p>
            <h2 className="font-black uppercase leading-[0.9]" style={{ fontSize:"clamp(2rem,4.5vw,4.5rem)" }}>
              Elegí tu plan.<br /><span className="text-gray-600">Empezá cuando querés.</span>
            </h2>
            <p className="text-sm text-gray-500 mt-3">Sin letra chica. Sin costos ocultos.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Plan Básico */}
            <div className="relative rounded-3xl border border-white/[0.08] p-7 flex flex-col hover:border-[#0074b7]/30 transition-all duration-300 group" style={{ background:"linear-gradient(145deg,rgba(0,116,183,0.06) 0%,#111 70%)" }}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#0074b7]/[0.06] rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"/>
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#0074b7] mb-1">Plan Básico</p>
              <div className="flex items-end gap-1.5 mb-1">
                <span className="text-5xl sm:text-6xl font-black text-white tracking-tight leading-none">$99</span>
                <div className="mb-1.5">
                  <span className="text-gray-400 font-medium text-sm">USD</span>
                  <span className="text-gray-500 text-xs">/mes</span>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-[#0074b7]/10 border border-[#0074b7]/20 text-[#0074b7] text-[10px] font-black px-2.5 py-1 rounded-full mb-4 w-fit">
                2 meses GRATIS — $990/año total
              </div>
              <p className="text-xs text-gray-500 mb-5 leading-relaxed">Para negocios que quieren su presencia digital sin romper el presupuesto. Pagás solo 10 meses, usás 12.</p>
              <div className="space-y-2.5 flex-1 mb-6">
                {[
                  { t:"Sitio web profesional personalizado", ok:true },
                  { t:"Hosting incluido", ok:true },
                  { t:"Soporte por email", ok:true },
                  { t:"Pago por cambios/ediciones (adicional)", ok:true },
                  { t:"Dominio .com propio", ok:false },
                  { t:"Empleado de IA Diseñador", ok:false },
                ].map(({t,ok}) => (
                  <div key={t} className="flex items-center gap-2.5 text-xs">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${ok?"bg-[#0074b7]/15":"bg-white/[0.04]"}`}>
                      {ok ? <Check size={10} className="text-[#0074b7]"/> : <X size={10} className="text-gray-600"/>}
                    </div>
                    <span className={ok?"text-gray-300":"text-gray-600 line-through"}>{t}</span>
                  </div>
                ))}
              </div>
              <MagBtn testId="plan-cta-basic" onClick={()=>handlePlan("basic")} disabled={checkoutLoading==="basic"}
                className="w-full py-3.5 rounded-xl font-black text-sm transition-all bg-[#0074b7] hover:bg-blue-500 text-white flex items-center justify-center gap-2 disabled:opacity-70">
                {checkoutLoading==="basic"?"Cargando...":<>Comenzar con Básico <ArrowRight size={14}/></>}
              </MagBtn>
              <p className="text-center text-[10px] text-gray-600 mt-2.5">2 meses gratis · $990 cobrado en un único pago anual</p>
            </div>

            {/* Plan Premium */}
            <div className="relative rounded-3xl p-7 pt-9 flex flex-col transition-all duration-300 hover:scale-[1.01]"
              style={{ background:"linear-gradient(145deg,rgba(255,199,0,0.1) 0%,#111 60%)", border:"1px solid rgba(255,199,0,0.25)" }}>
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 bg-[#ffc700] text-black text-[10px] font-black px-5 py-1 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg">Recomendado</div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#ffc700]/[0.07] rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none"/>
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#ffc700] mb-1 mt-2">Plan Premium</p>
              <div className="flex items-end gap-1.5 mb-1">
                <span className="text-5xl sm:text-6xl font-black text-white tracking-tight leading-none">$149</span>
                <div className="mb-1.5">
                  <span className="text-gray-400 font-medium text-sm">USD</span>
                  <span className="text-gray-500 text-xs">/mes</span>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-[#ffc700]/10 border border-[#ffc700]/20 text-[#ffc700] text-[10px] font-black px-2.5 py-1 rounded-full mb-4 w-fit">
                Cobrado mensualmente · Sin contrato
              </div>
              <p className="text-xs text-gray-400 mb-5 leading-relaxed">Todo lo que tu negocio necesita, con cambios ilimitados por IA. Sin permanencia mínima.</p>
              <div className="space-y-2.5 flex-1 mb-6">
                {[
                  "Dominio .com registrado a tu nombre",
                  "Hosting premium incluido",
                  "Empleado de IA Diseñador (cambios ilimitados)",
                  "100% adaptada a celulares",
                  "SSL/HTTPS y seguridad incluida",
                  "Soporte humano por WhatsApp",
                ].map(t=>(
                  <div key={t} className="flex items-center gap-2.5 text-xs text-gray-300">
                    <div className="w-4 h-4 rounded-full bg-[#ffc700]/15 flex items-center justify-center flex-shrink-0">
                      <Check size={10} className="text-[#ffc700]"/>
                    </div>
                    {t}
                  </div>
                ))}
              </div>
              <MagBtn testId="plan-cta-premium" onClick={()=>handlePlan("premium")} disabled={checkoutLoading==="premium"}
                className="w-full py-3.5 rounded-xl font-black text-sm transition-all bg-[#ffc700] hover:bg-[#e6b400] text-black flex items-center justify-center gap-2 btn-cta-pulse disabled:opacity-70">
                {checkoutLoading==="premium"?"Cargando...":<>Quiero el Plan Premium <ArrowRight size={14}/></>}
              </MagBtn>
              <p className="text-center text-[10px] text-gray-600 mt-2.5">Sin contrato · Sin permanencia · Cancelás cuando querés</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIOS ════════════════════════════════════════════════════════ */}
      <section ref={testimonialsRef} className="py-24 sm:py-32 section-darker relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className={`reveal-item text-center mb-14 ${testimonialsVis?"visible":""}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FF4F00] mb-3">Resultados reales</p>
            <h2 className="font-black uppercase" style={{ fontSize:"clamp(1.8rem,3.5vw,3.5rem)" }}>Lo que dicen los negocios</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t,i)=>(
              <div key={t.name} className={`reveal-item delay-${i+1} ${testimonialsVis?"visible":""} bg-[#111] border border-white/[0.06] rounded-2xl p-6 hover:border-white/[0.12] hover:bg-[#141414] transition-all`}>
                <div className="flex gap-0.5 mb-4">{Array.from({length:t.stars}).map((_,j)=><Star key={`${t.name}-star-${j}`} size={12} className="text-[#ffc700] fill-[#ffc700]"/>)}</div>
                <p className="text-sm text-gray-300 leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="pt-4 border-t border-white/[0.04]">
                  <p className="text-sm font-bold text-white">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.biz}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BANNER REVENDEDORES ═════════════════════════════════════════════════ */}
      <section className="py-16 px-5 section-dark">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-[#0074b7]/20 p-8 sm:p-10">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0074b7]/[0.06] to-transparent pointer-events-none"/>
            <div className="absolute top-0 left-0 w-48 h-48 bg-[#0074b7]/[0.08] rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none"/>
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#0074b7] mb-2">Programa Revendedores</p>
                <h3 className="font-black uppercase text-white mb-2" style={{ fontSize:"clamp(1.5rem,3vw,2.5rem)" }}>¿Tenés una agencia?</h3>
                <p className="text-sm text-gray-400 max-w-sm">Revendé páginas web con IA. Ganás el 100% del margen. Planes desde $199 USD/mes.</p>
              </div>
              <button onClick={() => { navigate("/resellers", { state: { scrollTop: true } }); }} data-testid="resellers-banner-btn"
                className="btn-fill-left flex-shrink-0 bg-[#0074b7] hover:bg-blue-500 text-white font-black px-7 py-3.5 rounded-xl text-sm transition-all flex items-center gap-2 whitespace-nowrap border border-[#0074b7]">
                Ver programa <ArrowRight size={15}/>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FOOTER INMERSIVO ═══════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-20 sm:py-28 section-darker noise">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"/>
        <div className="absolute inset-0 flex flex-col items-center justify-center select-none pointer-events-none"
          style={{ fontSize:"clamp(3rem,12vw,11rem)", lineHeight:0.85, fontWeight:900, letterSpacing:"-0.04em", color:"rgba(255,255,255,0.022)", textAlign:"center", textTransform:"uppercase" }}>
          <span>TU NEGOCIO</span>
          <span>EN INTERNET</span>
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center px-5">
          <div className="w-16 h-16 bg-[#ffc700]/10 border border-[#ffc700]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <TrendingUp size={28} className="text-[#ffc700]"/>
          </div>
          <h2 className="font-black uppercase leading-[0.9] mb-5" style={{ fontSize:"clamp(2rem,5vw,4.5rem)" }}>
            Elevá tu negocio.<br /><span className="text-[#ffc700]">En 5 minutos.</span>
          </h2>
          <p className="text-gray-400 mb-8 text-sm leading-relaxed max-w-md mx-auto">Mientras tu competencia espera, vos ya podés estar en Google captando clientes las 24 horas.</p>
          <MagBtn testId="final-cta-btn" onClick={() => document.getElementById("precio")?.scrollIntoView({behavior:"smooth"})}
            className="bg-[#FFC000] hover:bg-[#e6b000] active:scale-[0.98] text-black font-black px-10 py-4 rounded-2xl text-sm inline-flex items-center gap-2 btn-cta-pulse">
            Quiero aparecer en Google y hacer más clientes <ArrowRight size={18}/>
          </MagBtn>
          <p className="text-xs text-gray-600 mt-4">Desde $99/mes · Cancelás cuando querés</p>
        </div>
      </section>

      {/* ══ FOOTER ══════════════════════════════════════════════════════════════ */}
      <footer className="border-t border-white/[0.04] py-8 px-5 bg-[#080808]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6 flex-wrap">
          {/* Logo SitiosYA — solo imagen */}
          <img src="/logo.png" alt="SitiosYA" className="h-10 w-10 object-contain" onError={e=>{e.target.style.display="none";}}/>
          <div className="flex items-center gap-6 text-xs text-gray-500">
            <Link to="/resellers" className="hover:text-white transition-colors">Revendedores</Link>
            <button onClick={()=>navigate("/login")} className="hover:text-white transition-colors">Ingresar</button>
          </div>
          <p className="text-xs text-gray-600">© 2026 · Hecho con ❤️ por SitiosYA</p>
        </div>
      </footer>
    </div>
  );
}

// X is imported from lucide-react above
